import {
  Box,
  Button,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { type Permission, type Role, admin } from "../../data/api";
import { useAction, useLoad } from "../../data/data";
import { StatusBadge } from "../../ui/components/StatusBadge";
import {
  Failure,
  Field,
  FormRow,
  Mono,
  PageHeader,
  Section,
  Tag,
  Tags,
} from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";
import type { TenantPageProps } from "../pages";

// ---
// Roles — the bundles of permissions handed out
//
// Only the roles this caller may delegate are offered; the server bounds authoring the same way,
// so the form cannot ask for something that will be refused.
// ---

export function Roles({ tenant, token, scopes }: TenantPageProps): React.JSX.Element {
  const tokens = useTokens();
  const roles = useLoad(() => admin.roles(tenant, token), [tenant, token]);
  const permissions = useLoad(() => admin.permissions(tenant, token), [tenant, token]);
  const [key, setKey] = useState("");
  const [grants, setGrants] = useState<string[]>([]);
  const action = useAction(() => roles.reload());

  const dataPlane = (permissions.data ?? []).filter((p: Permission) => p.profile === "data-plane");

  return (
    <>
      <PageHeader title="Roles" />

      {scopes.includes("role:manage") && (
        <Section title="Create a tenant role">
          <FormRow>
            <Field label="Key" sx={{ flex: "1 1 220px", maxWidth: 320 }}>
              <TextField
                size="small"
                value={key}
                onChange={(event) => setKey(event.target.value)}
                placeholder="acme.order_reader"
              />
            </Field>
            <Field label="Permissions" sx={{ flex: "2 1 300px", maxWidth: 480 }}>
              <Select
                size="small"
                multiple
                displayEmpty
                value={grants}
                onChange={(event) =>
                  setGrants(
                    typeof event.target.value === "string"
                      ? [event.target.value]
                      : event.target.value,
                  )
                }
                renderValue={(selected) =>
                  selected.length === 0 ? (
                    <Box component="span" sx={{ color: tokens.ink.faint }}>
                      Choose permissions
                    </Box>
                  ) : (
                    selected.join(", ")
                  )
                }
              >
                {dataPlane.map((permission) => (
                  <MenuItem key={`${permission.audience}|${permission.key}`} value={permission.key}>
                    {permission.key}
                    {permission.machineOnly && (
                      <Box component="span" sx={{ ml: 2, color: tokens.ink.faint }}>
                        machine-only
                      </Box>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </Field>
            <Button
              variant="contained"
              disabled={key === "" || grants.length === 0}
              onClick={() =>
                action.run(async () => {
                  await admin.createRole(tenant, token, { key, grants });
                  setKey("");
                  setGrants([]);
                })
              }
            >
              Create role
            </Button>
          </FormRow>
          <Failure error={action.error} />
        </Section>
      )}

      <Section flush title={`Roles · ${roles.data?.roles.length ?? 0}`}>
        <Failure error={roles.error} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell>Grants</TableCell>
              <TableCell>Delegates</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.data?.roles.map((role: Role) => (
              <TableRow key={role.key} hover>
                <TableCell>
                  <Mono>{role.key}</Mono>
                </TableCell>
                <TableCell>
                  <StatusBadge
                    label={role.actorType}
                    tone={role.actorType === "machine" ? "info" : "neutral"}
                  />
                </TableCell>
                <TableCell>
                  <Tags>
                    {role.grants.map((grant) => (
                      <Tag key={grant} label={grant} />
                    ))}
                  </Tags>
                </TableCell>
                <TableCell>
                  {role.delegates.length === 0 ? (
                    <Box component="span" sx={{ color: tokens.ink.faint }}>
                      —
                    </Box>
                  ) : (
                    <Tags>
                      {role.delegates.map((delegate) => (
                        <Tag key={delegate} label={delegate} />
                      ))}
                    </Tags>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </>
  );
}
